import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import katex from 'katex/dist/contrib/auto-render.min.js';

const defaultOnLinkClick = (node) => {
  node.target = '_blank';
};

const addOnClickEventHandler = (contentBoxRef, onLinkClick) => {
  if (contentBoxRef.current.innerHTML && typeof onLinkClick === 'function') {
    const aTagList = contentBoxRef.current.querySelectorAll('a');
    aTagList.forEach((node) => onLinkClick(node));
  }
};

const youtubeUrlRegex = /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/;
const codeSandboxUrlRegex = /^(http(s)?:\/\/)?((w){3}.)?codesandbox\.io\/embed\/.+/;

const isAllowedIframeSource = (srcAttribute) =>
  srcAttribute && (youtubeUrlRegex.test(srcAttribute) || codeSandboxUrlRegex.test(srcAttribute));

DOMPurify.addHook('beforeSanitizeElements', (currentNode) => {
  if (currentNode.tagName === 'IFRAME') {
    const srcAttribute = currentNode.getAttribute('src');
    if (isAllowedIframeSource(srcAttribute)) {
      return currentNode;
    }
    currentNode.remove();
  }
});

const katexConfig = {
  delimiters: [
    { left: '$$', right: '$$', display: true },
    { left: '$', right: '$', display: false },
    { left: '\\(', right: '\\)', display: false },
    { left: '\\[', right: '\\]', display: true },
  ],
  throwOnError: false,
};

const KatexPreview = ({
  rawContent,
  children,
  className,
  style,
  onLinkClick = defaultOnLinkClick,
}) => {
  const sanitizedMath = DOMPurify.sanitize(rawContent, { ADD_TAGS: ['iframe'] });

  const ref = useRef(document.createElement('div'));
  useEffect(() => {
    addOnClickEventHandler(ref, onLinkClick);
  }, [onLinkClick, ref]);

  useEffect(() => {
    katex(ref.current, katexConfig);
  }, []);

  return (
    <Fragment>
      <div
        ref={ref}
        className={className}
        style={{
          width: '100%',
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedMath || children }}
      />
    </Fragment>
  );
};

KatexPreview.propTypes = {
  script: PropTypes.string,
  config: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object,
  renderLoading: PropTypes.func,
  isShowLoading: PropTypes.bool,
};
KatexPreview.defaultProps = {
  script:
    'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.6/MathJax.js?config=TeX-MML-AM_HTMLorMML',
};

export default KatexPreview;
